import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import goldenRatio from '@glass/common/modules/goldenRatio/goldenRatio';
import isValue from '@glass/common/modules/utils/isValue';
import Grid from '@glass/web/components/base/Unstable_Grid2';
import {
  RELATED_RESOURCES_SECTION_ID,
  RESOURCES_SECTION_ID,
} from '@glass/web/modules/content/pages/contentIds';
import useIsDocumentReadyPriorityLoadedIdleInView from '@glass/web/modules/loading/useIsDocumentReadyPriorityLoadedIdleInView';
import useLoadingResources from '@glass/web/modules/resources/useLoadingResources';

import ResourceCards, {
  breakpoints,
} from '@glass/shared/components/Content/ResourceCardHiddenContainer';
import ResourceLink from '@glass/shared/components/Link/ResourceLink';
import Section from '@glass/shared/components/Section/Section';
import SectionButtonCta from '@glass/shared/components/Section/SectionButtonCta';
import SectionSpacer, { BOTTOM_EDGE } from '@glass/shared/components/Section/SectionSpacer';
import { LAZY_LOADING, SSR_LOADING } from '@glass/shared/components/Section/loadingMethods';
import mergeRefs from '@glass/shared/helpers/utils/mergeRefs';
import blogTitle from '@glass/shared/modules/content/blogTitle';
import useBlogResources from '@glass/shared/modules/content/useBlogResources';
import useRelatedResources from '@glass/shared/modules/content/useRelatedResources';
import usePageData from '@glass/shared/modules/pageData/usePageData';
import withTrackingContext from '@glass/shared/modules/tracking/withTrackingContext';

const ResourcesSection = React.forwardRef(
  (
    {
      disableTags,
      variant,
      numLoadingCards,
      onLinkClick,
      className,
      spacing,
      title,
      subtitle,
      isNoXPadding,
      isAlternatingColor,
      limit,
      relatedTags: relatedTagsProp,
      loading: loadingProp,
      type,
      outerSpacing,
      disableCta,
      skip,
      loadingMethod, // note we still only fetch contentfulresources on the client
    },
    refProp,
  ) => {
    const isRelatedSection = type === RELATED_RESOURCES_SECTION_ID;

    const { refInView, isDocumentReadyPriorityLoadedIdleInView } =
      useIsDocumentReadyPriorityLoadedIdleInView({
        skip,
      });

    const { relatedResourceTags } = usePageData();
    const relatedTags = isRelatedSection ? relatedTagsProp || relatedResourceTags : relatedTagsProp;

    const { posts: resourcePosts, loading: resourcesLoading } = useBlogResources({
      limit,
      skip: Boolean(
        isRelatedSection ||
          (!isDocumentReadyPriorityLoadedIdleInView && loadingMethod !== SSR_LOADING) ||
          relatedTags?.length,
      ),
    });

    const { posts: relatedPosts, loading: relatedLoading } = useRelatedResources({
      relatedTags,
      limit,
      skip:
        (!isDocumentReadyPriorityLoadedIdleInView && loadingMethod !== SSR_LOADING) ||
        !relatedTags?.length,
    });

    const isLoading =
      !isDocumentReadyPriorityLoadedIdleInView || relatedLoading || resourcesLoading || loadingProp;
    const loadingPosts = useLoadingResources(limit);
    const dataPosts = resourcePosts?.length ? resourcePosts : relatedPosts;
    const posts = isLoading && !dataPosts?.length ? loadingPosts : dataPosts;
    const ref = useMemo(() => mergeRefs(refProp, refInView), [refProp, refInView]);

    if (!isLoading && !posts?.length) {
      return null;
    }

    return (
      <Section
        ref={ref}
        disableCta
        className={className}
        isAlternatingColor={isAlternatingColor}
        isNoXPadding={isNoXPadding}
        outerSpacing={outerSpacing}
        spacing={spacing}
        subtitle={subtitle}
        title={title}
        type={type}
      >
        <Grid container spacing={1}>
          {(isLoading && loadingPosts?.length ? loadingPosts : posts)?.map((blogPost, idx) => (
            <Grid
              key={blogPost?.slug || idx}
              lg={breakpoints.lg}
              md={breakpoints.md}
              sm={breakpoints.sm}
              xs={breakpoints.xs}
            >
              <ResourceCards
                blogPost={blogPost}
                disableTags={disableTags}
                idx={idx}
                loading={isLoading}
                maxNum={numLoadingCards}
                variant={variant}
                onClick={onLinkClick}
                onTagClick={onLinkClick}
              />
            </Grid>
          ))}
        </Grid>
        {!disableCta ? (
          <SectionSpacer
            edge={BOTTOM_EDGE}
            spacing={isValue(spacing) ? spacing / goldenRatio : undefined}
          >
            <SectionButtonCta component={ResourceLink} tag={relatedTags?.[0]}>
              Free career resources
            </SectionButtonCta>
          </SectionSpacer>
        ) : null}
      </Section>
    );
  },
);

ResourcesSection.defaultProps = {
  disableTags: false,
  title: blogTitle,
  subtitle: `$(brand.title) has the <b>tools</b> to $(verbWords.singular) the $(resumeWords.singular) to land your dream job`,
  limit: 8,
  disableCta: false,
  type: RESOURCES_SECTION_ID,
  loading: false,
  variant: 'outlined',
  loadingMethod: LAZY_LOADING,
};

ResourcesSection.propTypes = {
  className: PropTypes.string,
  disableCta: PropTypes.bool,
  disableTags: PropTypes.bool,
  isAlternatingColor: PropTypes.bool,
  isNoXPadding: PropTypes.bool,
  limit: PropTypes.number,
  loading: PropTypes.bool,
  loadingMethod: PropTypes.oneOf([LAZY_LOADING, SSR_LOADING]),
  numLoadingCards: PropTypes.number,
  onLinkClick: PropTypes.func,
  outerSpacing: PropTypes.number,
  relatedTags: PropTypes.arrayOf(PropTypes.string),
  skip: PropTypes.bool,
  spacing: PropTypes.number,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string,
  variant: PropTypes.string,
};

ResourcesSection.displayName = 'ResourcesSection';

export default React.memo(withTrackingContext(ResourcesSection, RESOURCES_SECTION_ID));
